import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import {
  getMotionAmplificationJob,
  getMotionAmplificationJobs,
  getMotionAmplificationUploadSessionStatusByJobId,
  type MotionAmplificationJob,
} from "../clients/motion-amplification";

const motionAmplificationJobsKey = ["motion-amplification", "jobs"] as const;
export const motionAmplificationJobsListKey = [
  ...motionAmplificationJobsKey,
  "list",
] as const;
export const motionAmplificationJobDetailKey = (
  jobId: MotionAmplificationJob["jobId"],
) => [...motionAmplificationJobsKey, "detail", jobId] as const;

const processingPollIntervalMs = 3000;
const jobsStaleTimeMs = 30_000;
const jobsPageSize = 1000;

function isProcessingStatus(status: MotionAmplificationJob["status"]) {
  return status === "Processing";
}

function shouldPollJob(status: MotionAmplificationJob["status"] | undefined) {
  return status === undefined || status === "Processing";
}

export function motionAmplificationJobQueryOptions(
  jobId: MotionAmplificationJob["jobId"],
) {
  return queryOptions({
    queryKey: motionAmplificationJobDetailKey(jobId),
    queryFn: async ({ client }) => {
      const job = await getMotionAmplificationJob(jobId);
      // Keep failed detail results available to the screen long enough for it
      // to show the retry toast. The jobs list intentionally omits failed
      // jobs, so invalidating it here can remove the processing row before the
      // screen observes the Failed transition.
      if (job?.status === "Completed") {
        await client.invalidateQueries({
          queryKey: motionAmplificationJobsListKey,
        });
      }
      return job;
    },
    refetchInterval: (query) =>
      shouldPollJob(query.state.data?.status)
        ? processingPollIntervalMs
        : false,
  });
}

export function motionAmplificationUploadSessionStatusQueryOptions(
  jobId: MotionAmplificationJob["jobId"],
) {
  return queryOptions({
    queryKey: [...motionAmplificationJobsKey, "upload-session", jobId],
    queryFn: () => getMotionAmplificationUploadSessionStatusByJobId(jobId),
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

export function useMotionAmplificationUploadSessionStatusQuery(
  jobId: MotionAmplificationJob["jobId"],
) {
  return useQuery(motionAmplificationUploadSessionStatusQueryOptions(jobId));
}

export const motionAmplificationJobsQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: motionAmplificationJobsListKey,
    queryFn: ({ pageParam }) =>
      getMotionAmplificationJobs({ limit: jobsPageSize, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: jobsStaleTimeMs,
  });

export function useMotionAmplificationJobsQuery() {
  const jobsQuery = useInfiniteQuery(motionAmplificationJobsQueryOptions());
  const listedJobs = jobsQuery.data?.pages.flatMap((page) => page.jobs);
  const processingJobIds =
    listedJobs
      ?.filter((job) => isProcessingStatus(job.status))
      .map((job) => job.jobId) ?? [];
  const processingJobQueries = useQueries({
    queries: processingJobIds.map(motionAmplificationJobQueryOptions),
  });
  const uploadPendingJobs =
    listedJobs?.filter((job) => job.status === "UploadPending") ?? [];
  const uploadSessionQueries = useQueries({
    queries: uploadPendingJobs.map((job) =>
      motionAmplificationUploadSessionStatusQueryOptions(job.jobId),
    ),
  });
  const currentJobsById = new Map(
    processingJobQueries
      .map((query) => query.data)
      .filter((job) => job !== undefined)
      .map((job) => [job.jobId, job]),
  );
  const jobs = listedJobs?.map((job) => currentJobsById.get(job.jobId) ?? job);

  const uploadSessionStatusByJobId = new Map(
    uploadPendingJobs.flatMap((job, index) => {
      const session = uploadSessionQueries[index]?.data?.session;
      return session ? [[job.jobId, session] as const] : [];
    }),
  );

  return { ...jobsQuery, data: jobs, uploadSessionStatusByJobId };
}
